import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllPages } from "@/lib/fetch-all-pages";
import { useAuth } from "@/lib/auth";

const ACTIVE_ORG_KEY = "vrg_active_org_id";

export type Organization = {
  id: string;
  name: string;
  created_by: string;
};

export type UserRole = "owner" | "manager" | "staff" | "viewer";

export type OrgMembership = {
  organization: Organization;
  role: UserRole;
};

export function useOrganization() {
  const { user } = useAuth();

  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_ORG_KEY)
  );

  const setActiveOrgId = useCallback((id: string) => {
    localStorage.setItem(ACTIVE_ORG_KEY, id);
    setActiveOrgIdState(id);
  }, []);

  const { data: allOrgs = [], isLoading } = useQuery({
    queryKey: ["organizations", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const roleData = await fetchAllPages<unknown>(() =>
        supabase
          .from("user_roles")
          .select("role, organization_id, organizations(*)")
          .eq("user_id", user.id)
          .order("organization_id")
      );

      if (!roleData || roleData.length === 0) return [];

      return roleData.map((r) => ({
        organization: r.organizations as unknown as Organization,
        role: r.role as UserRole,
      })) as OrgMembership[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Active membership: prefer stored org ID, fall back to first.
  // Memoized so consumers using `organization` in useEffect deps get a stable
  // reference across React Query refetches when the underlying data is unchanged.
  const activeMembership = useMemo(
    () => allOrgs.find((m) => m.organization?.id === activeOrgId) ?? allOrgs[0] ?? null,
    [allOrgs, activeOrgId],
  );

  return {
    organization: activeMembership?.organization ?? null,
    role: activeMembership?.role ?? null,
    allOrgs,
    activeOrgId: activeMembership?.organization?.id ?? null,
    setActiveOrgId,
    loading: isLoading && !!user,
  };
}
